import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import Navbar from '../components/Navbar';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Trophy, TrendingUp, Activity, Award, XCircle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AdminDashboard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // בדיקה שהמשתמש הוא אדמין
    if (!user) {
      toast.error('עליך להתחבר תחילה');
      navigate('/auth');
      return;
    }
    
    if (!user.is_admin) {
      // הפניה לדף הבית - בלי התנתקות אוטומטית
      toast.error('אין לך הרשאות אדמין');
      navigate('/');
      return;
    }

    fetchStatistics();
  }, [user, navigate]);

  const fetchStatistics = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/statistics`);
      setStatistics(response.data);
    } catch (error) {
      toast.error('שגיאה בטעינת סטטיסטיקות');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">טוען...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            👨‍💼 פאנל ניהול אדמין
          </h1>
          <p className="text-gray-600">שלום {user?.full_name}, ברוך הבא לפאנל הניהול</p>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            <Card className="glass-effect hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-gray-600 text-sm">סה"כ משתמשים</p>
                    <p className="text-3xl font-bold text-gray-900">{statistics.total_users}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {statistics.active_players} פעילים
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-effect hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <Trophy className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-gray-600 text-sm">תחרויות</p>
                    <p className="text-3xl font-bold text-gray-900">{statistics.total_tournaments}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {statistics.active_tournaments} פעילות
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-effect hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <Activity className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-gray-600 text-sm">משחקים</p>
                    <p className="text-3xl font-bold text-gray-900">{statistics.total_matches || 0}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {statistics.completed_tournaments} תחרויות הושלמו
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">

          <Card className="card-hover">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                ניהול משתמשים
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                צפייה ועריכת כל המשתמשים במערכת
              </p>
              <Link to="/admin/users" className="block">
                <div className="w-full bg-blue-600 hover:bg-blue-700 text-white text-center py-2 rounded-md font-medium">
                  ניהול משתמשים
                </div>
              </Link>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Trophy className="w-5 h-5" />
                ניהול תחרויות
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                יצירה, עריכה וסיום תחרויות
              </p>
              <Link to="/admin/tournaments" className="block">
                <div className="w-full bg-blue-600 hover:bg-blue-700 text-white text-center py-2 rounded-md font-medium">
                  ניהול תחרויות
                </div>
              </Link>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5" />
                עדכון תוצאות
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                עדכון תוצאות משחקים ונקודות שחקנים
              </p>
              <Link to="/admin/results" className="block">
                <div className="w-full bg-blue-600 hover:bg-blue-700 text-white text-center py-2 rounded-md font-medium">
                  עדכן תוצאות
                </div>
              </Link>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                סטטיסטיקות מתקדמות
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                דוחות וניתוחים מעמיקים
              </p>
              <Button 
                className="w-full btn-secondary"
                onClick={() => toast.info('בקרוב...')}
              >
                צפה בסטטיסטיקות
              </Button>
            </CardContent>
          </Card>

          <Link to="/admin/cancellations" className="block">
            <Card className="card-hover border-red-200 cursor-pointer hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-red-600">
                  <XCircle className="w-5 h-5" />
                  ביטולי הרשמות
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">
                  צפה בשחקנים שביטלו הרשמה לתחרויות
                </p>
                <div className="w-full bg-red-600 hover:bg-red-700 text-white text-center py-2 rounded-md font-medium">
                  צפה בביטולים
                </div>
              </CardContent>
            </Card>
          </Link>

          <Card className="card-hover border-purple-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-purple-600">
                <Activity className="w-5 h-5" />
                סנכרון סטטיסטיקות
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                חישוב מחדש של ניצחונות/הפסדים/תחרויות לכל השחקנים מהנתונים בפועל (ללא שינוי ניקוד)
              </p>
              <Button
                className="w-full bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="recompute-stats-btn"
                onClick={async () => {
                  if (!window.confirm('לחשב מחדש סטטיסטיקות לכל השחקנים? פעולה זו תעדכן ניצחונות/הפסדים/מספר תחרויות לכל המשתמשים על בסיס נתוני המשחקים בפועל. הניקוד לא ישתנה.')) return;
                  try {
                    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                    const response = await axios.post(
                      `${API}/api/admin/recompute-all-stats`,
                      {},
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    const d = response.data.details || {};
                    toast.success(`עודכנו ${d.users_updated || 0} שחקנים מתוך ${d.matches_analyzed || 0} משחקים`);
                  } catch (error) {
                    toast.error('שגיאה בחישוב מחדש: ' + (error.response?.data?.detail || error.message));
                  }
                }}
              >
                חשב מחדש סטטיסטיקות
              </Button>
            </CardContent>
          </Card>

          <Card className="card-hover border-indigo-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-indigo-600">
                <Activity className="w-5 h-5" />
                סגירת תחרויות שהסתיימו
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-4">
                סימון אוטומטי של תחרויות שמשחק הגמר שלהן הסתיים כ"הסתיים" (כולל סנכרון סטטיסטיקות)
              </p>
              <Button
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="auto-complete-tournaments-btn"
                onClick={async () => {
                  if (!window.confirm('לסמן את כל התחרויות שמשחק הגמר שלהן הסתיים כ"הסתיים"? פעולה זו לא תשנה ניקוד.')) return;
                  try {
                    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                    const response = await axios.post(
                      `${API}/api/admin/tournaments/auto-complete-finished`,
                      {},
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    const completed = response.data.completed || [];
                    toast.success(`${completed.length} תחרויות סומנו כהסתיימו`);
                  } catch (error) {
                    toast.error('שגיאה: ' + (error.response?.data?.detail || error.message));
                  }
                }}
              >
                סגור תחרויות שהסתיימו
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
